import type {
  Contact,
  ContactInput,
  ContactLogMethod,
  ContactPatch,
  ContactScope,
  ContactsAdapter,
  ContactsPage,
  ContactTemperature,
  ContactTemperatureSource,
  ImportOptions,
  ImportPreview,
  ImportRow,
} from "../types";
import { applyPatchToContact, loggedContact } from "../derive";

/**
 * Mock contacts adapter (W3a). Serves in-memory fixtures and mutates them
 * locally - NO Supabase, NO client writes, so the client-write scanner stays
 * green with zero suppressions. Export builds a CSV from the in-memory rows and
 * triggers a real client-side download (no backend), mirroring the live shape.
 */

const SELF = "me";

/** The fields that landed with the contacts wiring pass. Fixtures below predate
 *  them and are filled with these honest defaults, then overridden where a
 *  fixture wants to demonstrate a value. */
const CONTACT_DEFAULTS: Pick<
  Contact,
  | "temperatureSource"
  | "activeEngagement"
  | "linkedin"
  | "dateMet"
  | "contactCount"
  | "reminderTime"
  | "reminderReason"
> = {
  temperatureSource: "signal_strength",
  activeEngagement: false,
  linkedin: null,
  dateMet: null,
  contactCount: 0,
  reminderTime: null,
  reminderReason: null,
};

type SeedContact = Omit<Contact, keyof typeof CONTACT_DEFAULTS>;

function fill(c: SeedContact, extra: Partial<Contact> = {}): Contact {
  return { ...CONTACT_DEFAULTS, ...c, ...extra };
}

const FIXTURES: SeedContact[] = [
  {
    id: "c-001",
    name: "Sarah Chen",
    title: "VP Finance",
    company: "Meridian Freight",
    email: "sarah.chen@meridianfreight.com",
    phone: "+1 (415) 555-0142",
    temperature: "hot",
    source: "signal",
    matchedSignals: [
      {
        signalId: "s-mer",
        company: "Meridian Freight",
        headline: "RFP signal: near-term advisory exposure",
        sourceUrl: "https://example.com/meridian-rfp",
      },
      {
        signalId: "s-mer2",
        company: "Meridian Freight",
        headline: "Leadership change: new CFO mandate",
        sourceUrl: "https://example.com/meridian-cfo",
      },
    ],
    followUpDueAt: "2026-07-10",
    notes:
      "Met at the logistics summit - interested in restructuring advisory ahead of the refinancing.",
    lastTouchAt: "2026-07-02",
    ownerId: SELF,
    ownerName: "You",
  },
  {
    id: "c-002",
    name: "Marcus Webb",
    title: "Chief Operating Officer",
    company: "Northwind Materials",
    email: "m.webb@northwindmaterials.com",
    phone: "+1 (312) 555-0198",
    temperature: "warm",
    source: "signal",
    matchedSignals: [
      {
        signalId: "s-nor",
        company: "Northwind Materials",
        headline: "Transformation signal: supply-chain review",
      },
    ],
    followUpDueAt: null,
    notes: "Intro via Jordan. Evaluating a supply-chain operating review for FY27.",
    lastTouchAt: "2026-06-24",
    ownerId: SELF,
    ownerName: "You",
  },
  {
    id: "c-003",
    name: "Priya Nair",
    title: "General Counsel",
    company: "Aldridge Steel",
    email: "pnair@aldridgesteel.com",
    phone: "+1 (216) 555-0110",
    temperature: "hot",
    source: "manual",
    matchedSignals: [
      {
        signalId: "s-ald",
        company: "Aldridge Steel",
        headline: "M&A signal: near-term advisory exposure",
      },
    ],
    followUpDueAt: "2026-07-06",
    notes: "Litigation exposure on the pending deal - wants a call before the board meeting.",
    lastTouchAt: "2026-06-30",
    ownerId: SELF,
    ownerName: "You",
  },
  {
    id: "c-004",
    name: "Tom Bradley",
    title: "Director of Operations",
    company: "Beacon Health",
    email: "tom.bradley@beaconhealth.org",
    phone: null,
    temperature: "cold",
    source: "uploaded",
    matchedSignals: [],
    followUpDueAt: null,
    notes: "",
    lastTouchAt: "2026-05-18",
    ownerId: SELF,
    ownerName: "You",
  },
  {
    id: "c-005",
    name: "Elena Rossi",
    title: "Chief Financial Officer",
    company: "Larkspur Cosmetics",
    email: "erossi@larkspur.co",
    phone: "+1 (646) 555-0177",
    temperature: "warm",
    source: "signal",
    matchedSignals: [
      {
        signalId: "s-lar",
        company: "Larkspur Cosmetics",
        headline: "Hiring signal: near-term advisory exposure",
      },
    ],
    followUpDueAt: "2026-07-15",
    notes: "Budget cycle closes Q3 - revisit after the earnings call.",
    lastTouchAt: "2026-06-28",
    ownerId: SELF,
    ownerName: "You",
  },
  {
    id: "c-006",
    name: "David Okafor",
    title: "Head of Strategy",
    company: "Fairhaven Insurance",
    email: "d.okafor@fairhaven.com",
    phone: "+1 (860) 555-0133",
    temperature: "warm",
    source: "manual",
    matchedSignals: [],
    followUpDueAt: null,
    notes: "Former colleague - keep warm, good source of intros.",
    lastTouchAt: "2026-06-11",
    ownerId: SELF,
    ownerName: "You",
  },
  {
    id: "c-007",
    name: "Rachel Kim",
    title: "VP Procurement",
    company: "Calloway Industrial",
    email: "rachel.kim@calloway-ind.com",
    phone: null,
    temperature: "cold",
    source: "uploaded",
    matchedSignals: [],
    followUpDueAt: null,
    notes: "",
    lastTouchAt: null,
    ownerId: SELF,
    ownerName: "You",
  },
  {
    id: "c-008",
    name: "James Sullivan",
    title: "Senior Vice President",
    company: "Everest Logistics",
    email: "jsullivan@everestlog.com",
    phone: "+1 (206) 555-0159",
    temperature: "hot",
    source: "signal",
    matchedSignals: [
      {
        signalId: "s-eve",
        company: "Everest Logistics",
        headline: "RFP signal: near-term advisory exposure",
      },
    ],
    followUpDueAt: "2026-07-09",
    notes: "Active RFP with a competitor incumbent - move fast on the capabilities deck.",
    lastTouchAt: "2026-07-04",
    ownerId: SELF,
    ownerName: "You",
  },
  // Owned by other users - visible only in the admin "all" scope.
  {
    id: "c-009",
    name: "Nina Patel",
    title: "Chief Financial Officer",
    company: "Juniper Telecom",
    email: "npatel@junipertel.com",
    phone: "+1 (408) 555-0121",
    temperature: "warm",
    source: "signal",
    matchedSignals: [
      {
        signalId: "s-jun",
        company: "Juniper Telecom",
        headline: "Target-account signal: near-term advisory exposure",
      },
    ],
    followUpDueAt: "2026-07-12",
    notes: "Strategic target account - coordinated pursuit with the TMT practice.",
    lastTouchAt: "2026-07-01",
    ownerId: "b83a763d-df3c-489f-9d6f-becbb68ae8a2",
    ownerName: "Casey Colleague",
  },
  {
    id: "c-010",
    name: "Robert Frost",
    title: "VP Legal",
    company: "Cedar Point Energy",
    email: "rfrost@cedarpointenergy.com",
    phone: null,
    temperature: "cold",
    source: "uploaded",
    matchedSignals: [],
    followUpDueAt: null,
    notes: "Dormant - no active mandate.",
    lastTouchAt: "2026-04-22",
    ownerId: "9f2c1a44-7b6e-4d21-9c3a-1e5f8b0d7a90",
    ownerName: "Jordan Lee",
  },
  {
    id: "c-011",
    name: "Amy Zhang",
    title: "Director of Finance",
    company: "Vantage Group",
    email: "azhang@vantagegroup.com",
    phone: "+1 (503) 555-0188",
    temperature: "hot",
    source: "signal",
    matchedSignals: [
      {
        signalId: "s-van",
        company: "Vantage Group",
        headline: "Transformation signal: near-term advisory exposure",
      },
    ],
    followUpDueAt: "2026-07-08",
    notes: "Warm intro landed - proposal due next week.",
    lastTouchAt: "2026-07-05",
    ownerId: "b83a763d-df3c-489f-9d6f-becbb68ae8a2",
    ownerName: "Casey Colleague",
  },
];

/**
 * Provenance labels for the card source tag (legacy parity: "Web Summit 2025",
 * "LinkedIn Contacts"). Mock-first - the live adapter derives the tag from the
 * `event_source` column until a richer backend provenance field lands.
 */
const PROVENANCE_BY_SOURCE: Record<Contact["source"], string[]> = {
  uploaded: ["Web Summit 2025", "LinkedIn Contacts", "CSV upload"],
  signal: ["Signal match", "Web Summit 2025"],
  manual: ["Manual entry", "Referral"],
};

/** Fill a deterministic source tag for any fixture that doesn't declare one. */
function withProvenance(c: SeedContact, i: number): SeedContact {
  if (c.sourceDetail) return c;
  const pool = PROVENANCE_BY_SOURCE[c.source];
  return { ...c, sourceDetail: pool[i % pool.length] };
}

// Extra own-scope contacts so the 28-per-page "Show More" pagination is
// exercisable in mock mode (the 8 hand-authored own contacts alone never spill
// a second page). Deterministic - no Math.random.
const GEN_FIRST = [
  "Ava",
  "Liam",
  "Noah",
  "Mia",
  "Ethan",
  "Zoe",
  "Owen",
  "Lucy",
  "Ian",
  "Ruby",
  "Cole",
  "Nora",
  "Seth",
  "Iris",
  "Wade",
  "June",
  "Dean",
  "Faye",
  "Reed",
  "Tara",
];
const GEN_LAST = [
  "Brooks",
  "Hayes",
  "Ford",
  "Nash",
  "Vance",
  "Pope",
  "Quinn",
  "Wells",
  "Barr",
  "Cross",
  "Lowe",
  "Frye",
  "Beck",
  "Dodd",
  "Sharp",
  "Gill",
  "Roe",
  "Kane",
  "Best",
  "Lund",
];
const GEN_TITLE = [
  "VP Finance",
  "Chief Operating Officer",
  "General Counsel",
  "Head of Strategy",
  "Director of Operations",
  "Treasurer",
  "VP Procurement",
  "Chief Restructuring Officer",
];
const GEN_COMPANY = [
  "Halcyon Metals",
  "Bristol Foods",
  "Quorum Health",
  "Sable Energy",
  "Ridgeline Air",
  "Piedmont Bank",
  "Wexford Retail",
  "Ostara Bio",
  "Tamarind Media",
  "Corvus Freight",
  "Marlowe Textiles",
  "Delphi Motors",
  "Kestrel Mining",
  "Ansel Robotics",
  "Verity Payments",
  "Solstice Hotels",
  "Orion Chemicals",
  "Brightwater Utilities",
  "Cobalt Aerospace",
  "Meadowlark Grocers",
];
const GEN_TEMP: ContactTemperature[] = ["hot", "warm", "cold"];
const GEN_SOURCE: Contact["source"][] = ["signal", "manual", "uploaded"];

function generatedContacts(n: number): Contact[] {
  return Array.from({ length: n }).map((_, i) => {
    const first = GEN_FIRST[i % GEN_FIRST.length]!;
    const last = GEN_LAST[(i * 7) % GEN_LAST.length]!;
    const name = `${first} ${last}`;
    const company = GEN_COMPANY[i % GEN_COMPANY.length]!;
    const source = GEN_SOURCE[i % GEN_SOURCE.length]!;
    const temperature = GEN_TEMP[(i * 5) % GEN_TEMP.length]!;
    const emailCo = company.split(" ")[0]!.toLowerCase();
    return fill(
      {
        id: `c-g${String(i + 1).padStart(2, "0")}`,
        name,
        title: GEN_TITLE[(i * 3) % GEN_TITLE.length]!,
        company,
        email: `${first.toLowerCase()}.${last.toLowerCase()}@${emailCo}.com`,
        phone: i % 3 === 0 ? null : `+1 (${200 + (i % 700)}) 555-0${100 + i}`,
        temperature,
        source,
        sourceDetail: PROVENANCE_BY_SOURCE[source][i % PROVENANCE_BY_SOURCE[source].length]!,
        matchedSignals:
          source === "signal"
            ? [
                {
                  signalId: `s-g${i}`,
                  company,
                  headline: `Signal match: near-term advisory exposure`,
                },
              ]
            : [],
        followUpDueAt: i % 4 === 0 ? `2026-07-${String(10 + (i % 18)).padStart(2, "0")}` : null,
        notes: i % 5 === 0 ? "Follow up after the next planning cycle." : "",
        lastTouchAt: null,
        ownerId: SELF,
        ownerName: "You",
      },
      { contactCount: i % 4 === 0 ? 2 : 0 }
    );
  });
}

const SEED: Contact[] = [
  ...FIXTURES.map((c, i) => fill(withProvenance(c, i))),
  ...generatedContacts(28),
];

const clone = (c: Contact): Contact => ({
  ...c,
  matchedSignals: c.matchedSignals.map((m) => ({ ...m })),
});

function csvCell(v: string): string {
  return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
}

function buildCsv(rows: Contact[]): string {
  const header = [
    "Name",
    "Title",
    "Company",
    "Email",
    "Phone",
    "Temperature",
    "Source",
    "Matched signals",
    "Follow-up due",
    "Last touch",
    "Owner",
    "Notes",
  ];
  const lines = rows.map((c) =>
    [
      c.name,
      c.title,
      c.company,
      c.email,
      c.phone ?? "",
      c.temperature,
      c.source,
      String(c.matchedSignals.length),
      c.followUpDueAt ?? "",
      c.lastTouchAt ?? "",
      c.ownerName,
      c.notes,
    ]
      .map((v) => csvCell(String(v)))
      .join(",")
  );
  return [header.join(","), ...lines].join("\n");
}

function triggerCsvDownload(csv: string, filename: string): string {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  if (typeof document !== "undefined") {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  }
  return url;
}

const delay = <T>(value: T, ms = 120): Promise<T> =>
  new Promise((resolve) => setTimeout(() => resolve(value), ms));

/** Split one CSV line into fields, honouring double-quoted values. */
function parseCsvLine(line: string): string[] {
  const out: string[] = [];
  let cur = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          cur += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        cur += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ",") {
      out.push(cur);
      cur = "";
    } else {
      cur += ch;
    }
  }
  out.push(cur);
  return out.map((c) => c.trim());
}

/** CSV columns the mock preview understands (a subset of the create fields). */
type ImportColumn =
  | "name"
  | "title"
  | "company"
  | "email"
  | "phone"
  | "temperature"
  | "notes"
  | "followUpDueAt"
  | "linkedin"
  | "eventSource"
  | "dateMet";

const COLUMN_ALIASES: Record<ImportColumn, string[]> = {
  name: ["name", "full name", "contact"],
  title: ["title", "role", "job title"],
  company: ["company", "account", "organization", "organisation"],
  email: ["email", "e-mail"],
  phone: ["phone", "mobile", "tel"],
  temperature: ["temperature", "temp"],
  notes: ["notes", "note", "comment"],
  followUpDueAt: ["follow-up", "follow up", "followup"],
  linkedin: ["linkedin", "linkedin url", "linkedin_url"],
  eventSource: ["event", "source", "event source", "event_source", "met at"],
  dateMet: ["date met", "date_met", "met on"],
};

const COLUMN_ORDER: ImportColumn[] = [
  "name",
  "title",
  "company",
  "email",
  "phone",
  "temperature",
  "notes",
  "followUpDueAt",
  "linkedin",
  "eventSource",
  "dateMet",
];

/** Map a header row to column indices; falls back to positional order. */
function resolveColumns(header: string[]): Record<ImportColumn, number> {
  const lower = header.map((h) => h.toLowerCase());
  const idx = {} as Record<ImportColumn, number>;
  COLUMN_ORDER.forEach((key, position) => {
    const found = lower.findIndex((h) => COLUMN_ALIASES[key].includes(h));
    idx[key] = found >= 0 ? found : -1;
    // Positional fallback only when the header is clearly not labelled.
    if (found < 0 && !lower.some((h) => h === "name" || h === "email")) {
      idx[key] = position;
    }
  });
  return idx;
}

function normalizeTemp(raw: string): ContactTemperature {
  const t = raw.toLowerCase();
  return t === "hot" || t === "warm" || t === "cold" ? t : "warm";
}

/** Build an uploaded contact from an import row's parsed input. */
function uploadedContact(
  input: ContactInput,
  id: string,
  group: string | null,
  ownerId = SELF
): Contact {
  const source: ContactTemperatureSource = "signal_strength";
  return {
    id,
    name: input.name,
    title: input.title,
    company: input.company,
    email: input.email,
    phone: input.phone,
    temperature: input.temperature,
    temperatureSource: source,
    activeEngagement: false,
    source: "uploaded",
    sourceDetail: group ?? input.eventSource ?? null,
    linkedin: input.linkedin ?? null,
    dateMet: input.dateMet ?? null,
    matchedSignals: [],
    followUpDueAt: input.followUpDueAt,
    reminderTime: null,
    reminderReason: null,
    notes: input.notes,
    lastTouchAt: null,
    contactCount: 0,
    ownerId,
    ownerName: ownerId === SELF ? "You" : "Selected consultant",
  };
}

export class MockContactsAdapter implements ContactsAdapter {
  private store: Contact[];
  private seq = 100;

  constructor(seed: Contact[] = SEED) {
    this.store = seed.map(clone);
  }

  async listContactsPage(
    scope: ContactScope,
    _viewerId: string,
    opts: { limit: number; offset: number }
  ): Promise<ContactsPage> {
    const rows = scope === "all" ? this.store : this.store.filter((c) => c.ownerId === SELF);
    const page = rows.slice(opts.offset, opts.offset + opts.limit).map(clone);
    return delay({ contacts: page, count: rows.length });
  }

  async addContact(input: ContactInput): Promise<Contact> {
    const created = uploadedContact(input, `c-${++this.seq}`, input.eventSource ?? null);
    created.source = "manual";
    this.store = [created, ...this.store];
    return delay(clone(created));
  }

  async updateContact(id: string, patch: ContactPatch): Promise<Contact> {
    let updated: Contact | null = null;
    this.store = this.store.map((c) => {
      if (c.id !== id) return c;
      updated = applyPatchToContact(c, patch);
      return updated;
    });
    if (!updated) throw new Error(`Contact ${id} not found`);
    return delay(clone(updated));
  }

  async logContact(id: string, _method: ContactLogMethod): Promise<void> {
    const stamp = new Date().toISOString().slice(0, 10);
    this.store = this.store.map((c) => (c.id === id ? loggedContact(c, stamp) : c));
    return delay(undefined);
  }

  async deleteContact(id: string): Promise<void> {
    this.store = this.store.filter((c) => c.id !== id);
    return delay(undefined);
  }

  async exportCsv(scope: ContactScope): Promise<{ url: string }> {
    const rows = scope === "all" ? this.store : this.store.filter((c) => c.ownerId === SELF);
    const filename = `contacts-${scope}-${rows.length}.csv`;
    const url = triggerCsvDownload(buildCsv(rows), filename);
    return { url };
  }

  async previewCsvImport(
    file: File,
    _viewerId: string,
    options: ImportOptions
  ): Promise<ImportPreview> {
    const text = await file.text();
    const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
    const targetOwner = options.ownerUserId ?? SELF;
    const own = this.store.filter((c) => c.ownerId === targetOwner);
    const rows: ImportRow[] = [];

    if (lines.length === 0) {
      return {
        filename: file.name,
        sourceText: text,
        rows,
        summary: { total: 0, valid: 0, duplicate: 0, excluded: 0, error: 0 },
        options,
      };
    }

    const firstFields = parseCsvLine(lines[0]!);
    const hasHeader = firstFields
      .map((f) => f.toLowerCase())
      .some((f) => ["name", "email", "company", "title"].includes(f));
    const cols = hasHeader
      ? resolveColumns(firstFields)
      : ({
          name: 0,
          title: 1,
          company: 2,
          email: 3,
          phone: 4,
          temperature: 5,
          notes: 6,
          followUpDueAt: 7,
          linkedin: 8,
          eventSource: 9,
          dateMet: 10,
        } as Record<ImportColumn, number>);
    const dataLines = hasHeader ? lines.slice(1) : lines;

    const seenEmails = new Set<string>();
    dataLines.forEach((line, i) => {
      const fields = parseCsvLine(line);
      const at = (key: ImportColumn) => (cols[key] >= 0 ? (fields[cols[key]] ?? "").trim() : "");
      const email = at("email");
      const input: ContactInput = {
        name: at("name"),
        title: at("title"),
        company: at("company"),
        email,
        phone: at("phone") || null,
        temperature: normalizeTemp(at("temperature")),
        followUpDueAt: at("followUpDueAt") || null,
        notes: at("notes"),
        linkedin: at("linkedin") || null,
        eventSource: at("eventSource") || null,
        dateMet: at("dateMet") || null,
      };

      let status: ImportRow["status"] = "valid";
      let message = "";
      const emailKey = email.toLowerCase();
      const nameCo = `${input.name} ${input.company}`.toLowerCase();

      const sourceRow = i + (hasHeader ? 2 : 1);
      const excludedByTitle = options.filters.excludeKeywords.some((keyword) =>
        input.title.toLowerCase().includes(keyword.toLowerCase())
      );
      if (options.filters.excludeRowNumbers.includes(sourceRow)) {
        status = "excluded";
        message = "Excluded from this upload";
      } else if (excludedByTitle) {
        status = "excluded";
        message = "Excluded by title filters";
      } else if (!input.name) {
        status = "error";
        message = "Missing name";
      } else if (email && !email.includes("@")) {
        status = "error";
        message = "Invalid email";
      } else if (
        (emailKey && own.some((c) => c.email.toLowerCase() === emailKey)) ||
        own.some((c) => `${c.name} ${c.company}`.toLowerCase() === nameCo)
      ) {
        status = "duplicate";
        message = "Already in your contacts";
      } else if (emailKey && seenEmails.has(emailKey)) {
        status = "duplicate";
        message = "Duplicate row in file";
      }
      if (emailKey) seenEmails.add(emailKey);

      rows.push({ line: sourceRow, input, status, message });
    });

    const summary = {
      total: rows.length,
      valid: rows.filter((r) => r.status === "valid").length,
      duplicate: rows.filter((r) => r.status === "duplicate").length,
      excluded: rows.filter((r) => r.status === "excluded").length,
      error: rows.filter((r) => r.status === "error").length,
    };
    return delay({ filename: file.name, sourceText: text, rows, summary, options });
  }

  async confirmCsvImport(preview: ImportPreview): Promise<{ imported: number }> {
    const group = preview.options.sourceGroup;
    const valid = preview.rows.filter((r) => r.status === "valid");
    const ownerId = preview.options.ownerUserId ?? SELF;
    const created = valid.map((r) => uploadedContact(r.input, `c-${++this.seq}`, group, ownerId));
    this.store = [...created, ...this.store];
    return delay({ imported: created.length });
  }
}

export function createMockContactsAdapter(seed?: Contact[]): MockContactsAdapter {
  return new MockContactsAdapter(seed);
}
